'use client';

import { c as _c } from "react/compiler-runtime";
import Image from 'next/image';
import { cn } from '@/lib/utils';
import type { ModelPickerOption } from '@/types/ai';
export type ProviderKey = ModelPickerOption['provider'];
interface ProviderLogoProperties {
  className?: string;
  tone?: 'light' | 'dark';
}
const providerDisplayNames: Record<ProviderKey, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  google: 'Google',
  meta: 'Meta',
  xai: 'xAI',
  mistral: 'Mistral AI',
  custom: 'Custom Provider'
};
const providerLogoSources: Partial<Record<ProviderKey, string>> = {
  openai: '/provider-logos/openai.svg',
  anthropic: '/provider-logos/anthropic.svg',
  google: '/provider-logos/google.svg',
  meta: '/provider-logos/meta.svg',
  xai: '/provider-logos/xai.svg',
  mistral: '/provider-logos/mistral.svg'
};
const fallbackMonograms: Record<ProviderKey, string> = {
  openai: 'OA',
  anthropic: 'A',
  google: 'G',
  meta: 'M',
  xai: 'xAI',
  mistral: 'MI',
  custom: '?'
};
export function ProviderLogo(t0) {
  const $ = _c(16);
  const {
    provider,
    className,
    tone: t1
  } = t0;
  const tone = t1 === undefined ? "light" : t1;
  const logoSource = providerLogoSources[provider];
  if (logoSource) {
    let t2;
    if ($[0] !== className) {
      t2 = cn("h-5 w-5 object-contain", className);
      $[0] = className;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    let t3;
    if ($[2] !== tone) {
      t3 = tone === "dark" ? {
        filter: "brightness(0) invert(1)"
      } : undefined;
      $[2] = tone;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    let t4;
    if ($[4] !== logoSource || $[5] !== t2 || $[6] !== t3) {
      t4 = <Image src={logoSource} alt="" aria-hidden={true} width={20} height={20} sizes="32px" draggable={false} className={t2} style={t3} />;
      $[4] = logoSource;
      $[5] = t2;
      $[6] = t3;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    return t4;
  }
  const monogram = fallbackMonograms[provider] ?? fallbackMonograms.custom;
  const t2 = monogram.length > 2 ? "text-[0.55rem]" : null;
  const t3 = tone === "dark" ? "text-white" : "text-black";
  let t4;
  if ($[8] !== className || $[9] !== t2 || $[10] !== t3) {
    t4 = cn("flex h-5 w-5 items-center justify-center rounded-sm text-[0.65rem] font-semibold uppercase tracking-tight", t2, t3, className);
    $[8] = className;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const t5 = providerDisplayNames[provider] ?? providerDisplayNames.custom;
  let t6;
  if ($[12] !== monogram || $[13] !== t4 || $[14] !== t5) {
    t6 = <span role="img" className={t4} aria-label={t5}>{monogram}</span>;
    $[12] = monogram;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}